import { Alert, CircularProgress, Icon, List } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiInput from "components/base/UiInput";
import UiTypography from "components/base/UiTypography";
import { WorksheetProduct } from "generated/graphql";
import useGetWorksheetProducts from "hooks/worksheetProducts/useGetWorksheetProducts";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import WorksheetCatalogRow from "modules/proposals/worksheet/WorksheetCatalogRow";
import useDebouncedSearchTerm from "hooks/strings/useDebouncedSearchTerm";

type WorksheetProductSectionProps = {
  open: boolean;
  disabled?: boolean;
  onAddBlank: () => void;
  onSelect: (worksheetProductId?: string) => void;
};

export default function WorksheetProductSection({
  open,
  disabled,
  onAddBlank,
  onSelect,
}: WorksheetProductSectionProps) {
  const { searchTerm, setSearchTerm, debouncedSearchTerm } = useDebouncedSearchTerm("");
  const {
    data: worksheetProducts,
    loading: worksheetProductsLoading,
    error: worksheetProductsError,
    refetch: refetchWorksheetProducts,
  } = useGetWorksheetProducts(
    {
      first: 100,
      search: debouncedSearchTerm || undefined,
    },
    { skip: !open, fetchPolicy: "network-only" }
  );

  return (
    <>
      <MDBox display="flex" alignItems="center" justifyContent="space-between" gap="8px">
        <UiTypography uiVariant="heading-sm">Worksheet Products</UiTypography>
        <UiButton uiVariant="outlined-small" disabled={disabled} onClick={onAddBlank}>
          <Icon fontSize="small">add</Icon>
          Add blank product
        </UiButton>
      </MDBox>

      <UiInput
        value={searchTerm}
        placeholder="Search worksheet products"
        onChange={(event) => setSearchTerm(event.target.value)}
      />

      {worksheetProductsError && (
        <Alert
          severity="error"
          action={
            <UiButton uiVariant="outlined-small" onClick={() => refetchWorksheetProducts()}>
              Retry
            </UiButton>
          }
        >
          Worksheet products could not load: {worksheetProductsError.message}
        </Alert>
      )}
      {worksheetProductsLoading ? (
        <MDBox display="flex" justifyContent="center" p="8px">
          <CircularProgress size={24} />
        </MDBox>
      ) : (
        <List dense sx={{ maxHeight: 260, overflow: "auto" }}>
          {worksheetProducts.map((product) => (
            <WorksheetCatalogRow
              key={product.id}
              product={product}
              disabled={disabled}
              onSelect={onSelect}
            />
          ))}
          {worksheetProducts.length === 0 && (
            <UiTypography
              uiVariant="body-sm"
              sx={{ color: draftColors.textSecondary, textAlign: "center", py: "8px" }}
            >
              No saved worksheet products yet
            </UiTypography>
          )}
        </List>
      )}
    </>
  );
}
